import { Avatar, Box, pulseTheme, Tooltip } from '@pulse/ui';
import type { AvatarProps } from '@pulse/ui';
import { forwardRef } from 'react';
import type { ForwardRefExoticComponent, ReactElement, ReactNode, RefAttributes } from 'react';

export type UserAvatarSize = 'small' | 'medium' | 'large' | 'extraLarge';

type ThemeBreakpoint = keyof typeof pulseTheme.breakpoints.values;
type ResponsiveSize = Pick<Record<ThemeBreakpoint, number>, 'xs' | 'md' | 'lg'>;

const SIZE_STYLES: Record<UserAvatarSize, { width: ResponsiveSize; height: ResponsiveSize; fontSize: string }> = {
  small: { width: { xs: 24, md: 22, lg: 24 }, height: { xs: 24, md: 22, lg: 24 }, fontSize: '0.75rem' },
  medium: { width: { xs: 26, md: 24, lg: 26 }, height: { xs: 26, md: 24, lg: 26 }, fontSize: '0.875rem' },
  large: { width: { xs: 32, md: 30, lg: 32 }, height: { xs: 32, md: 30, lg: 32 }, fontSize: '1rem' },
  extraLarge: { width: { xs: 40, md: 36, lg: 40 }, height: { xs: 40, md: 36, lg: 40 }, fontSize: '1rem' },
};

export interface UserAvatarProps extends Omit<AvatarProps, 'children'> {
  name: string;
  size?: UserAvatarSize;
}

export function userAvatarSizeSx(size: UserAvatarSize): { width: ResponsiveSize; height: ResponsiveSize; fontSize: string } {
  return SIZE_STYLES[size];
}

type UserAvatarComponent = ForwardRefExoticComponent<UserAvatarProps & RefAttributes<HTMLDivElement>> & {
  small: UserAvatarSize;
  medium: UserAvatarSize;
  large: UserAvatarSize;
  extraLarge: UserAvatarSize;
};

// forwardRef so Tooltip (and anything else needing a DOM ref) can attach directly to a
// UserAvatar — without this, wrapping a UserAvatar in Tooltip silently fails to
// position/attach hover listeners, forcing every call site to add its own `<span>` workaround.
export const UserAvatar = forwardRef<HTMLDivElement, UserAvatarProps>(({ name, size = 'medium', sx, ...rest }, ref) => (
  <Avatar ref={ref} sx={{ ...SIZE_STYLES[size], ...sx }} {...rest}>
    {name.trim().charAt(0).toUpperCase() || '?'}
  </Avatar>
)) as UserAvatarComponent;

UserAvatar.displayName = 'UserAvatar';

// Lets call sites write size={UserAvatar.medium} instead of the bare string 'medium'.
UserAvatar.small = 'small';
UserAvatar.medium = 'medium';
UserAvatar.large = 'large';
UserAvatar.extraLarge = 'extraLarge';

export interface UserAvatarOverflowProps {
  /** How many additional people this badge stands in for, rendered as "+N". */
  count: number;
  size?: UserAvatarSize;
  /** Hover content — typically the overflowed people's names. Omit for no tooltip. */
  tooltip?: ReactNode;
}

// The "+N" badge that caps off a UserAvatar stack (e.g. an issue's Assignees) when there are
// more people than the stack shows individually. Not itself a UserAvatar — it needs to display a
// count, and UserAvatar always renders just the first character of a `name` — but sized via
// userAvatarSizeSx to be pixel-identical to a real UserAvatar of the same `size`, so it reads as
// part of the same stack rather than a mismatched extra element.
export function UserAvatarOverflow({ count, size = 'medium', tooltip }: UserAvatarOverflowProps): ReactElement {
  const badge = (
    <Avatar
      sx={{
        ...SIZE_STYLES[size],
        bgcolor: 'action.hover',
        color: 'text.secondary',
        border: '1.5px solid',
        borderColor: 'background.paper',
      }}
    >
      +{count}
    </Avatar>
  );
  if (!tooltip) return badge;
  return (
    <Tooltip
      title={
        <Box sx={{ maxWidth: 140, maxHeight: 120, overflowY: 'auto', wordBreak: 'break-word' }}>
          {tooltip}
        </Box>
      }
    >
      {badge}
    </Tooltip>
  );
}
